import React, { useState, useEffect, useRef, useMemo } from 'react';
import Helmet from 'react-helmet';
import Axios from 'axios';
import { useLocation } from 'react-router-dom';
import getQueryVariable from '../../utils/getQueryVariable';

import BlogCard from '../../components/BlogCard';
import SNSPanel from '../../components/SNSPanel';
import { Reset } from '../../components/Icons';

import './index.scss';

const DEFAULT_QUERY = {
    page: 1,
    category: null,
    tag: null
}


const BlogList = ({ wording }) => {
    const _w = wording;
    const location = useLocation();

    const autoload = useRef(null);
    const [blogs, setBlogs] = useState(null);
    const [categories, setCategories] = useState(null);
    const [tags, setTags] = useState(null);

    const [currentQuery, setCurrentQuery] = useState(null);

    const currentQueryRef = useRef();
    const isLoadingRef = useRef(false);
    const [loadingAnimation, setLoadingAnimation] = useState(false);
    const pageDataRef = useRef();

    const currentTagLabel = useMemo(() => {
        if (!tags?.length) return;
        if (!currentQuery?.tag) return;
        let obj = tags.find((tag) => tag.id == currentQuery.tag);
        if (obj) return obj.name;
        return;

    }, [tags, currentQuery]);

    /**
     * getBlogs - 根據參數取得部落格列表
     * @param {object} params - { page: 1, category: 1, tag: 1}
     */
    const getBlogs = async (params) => {
        let searchStr = "?";
        let variables = [];
        if (params) {
            for (let key in params) {
                if (params[key] !== null) {
                    variables.push(`${key}=${params[key]}`);
                }
            }
            searchStr += variables.join("&");
        } else {
            searchStr = "";
        }

        isLoadingRef.current = true;
        setLoadingAnimation(true);

        const result = await Axios.get(`/api/blogs${searchStr}`);
        const { data, last_page, current_page } = result.data;
        if (params.page > 1) {
            setBlogs(blogs.concat(data));
        } else {
            setBlogs(data);
        }

        pageDataRef.current = { last_page, current_page };

        setLoadingAnimation(false);
        isLoadingRef.current = false;

    }

    const getCategories = async () => {
        const res = await Axios.get(`/api/blog-categories`);
        setCategories(res.data)

    }

    const getTags = async () => {
        const res = await Axios.get(`/api/blog-tags`);
        setTags(res.data)
    }

    const autoLoadWhenScrollBottom = () => {
        if (!pageDataRef.current) return;
        if (window.scrollY + window.innerHeight > autoload.current.offsetTop) {
            if (currentQueryRef.current.page < pageDataRef.current.last_page) {
                setCurrentQuery({ ...currentQueryRef.current, page: currentQueryRef.current.page + 1 })
            }
        }
    }

    useEffect(() => {
        const currentSearch = location.search;
        const category = getQueryVariable(currentSearch, "category") || null;
        const tag = getQueryVariable(currentSearch, "tag") || null;

        let params;
        if (category || tag) {
            params = { page: 1, category: category, tag: tag };
            setCurrentQuery(params);
        } else {
            setCurrentQuery(DEFAULT_QUERY);
        }
        getCategories();
        getTags();

        window.addEventListener('scroll', autoLoadWhenScrollBottom);

        return () => {
            window.removeEventListener('scroll', autoLoadWhenScrollBottom);
        }
    }, []);

    useEffect(() => {
        if (!currentQuery) return;
        if (isLoadingRef.current) return;
        getBlogs(currentQuery);
        currentQueryRef.current = currentQuery;
    }, [currentQuery]);


    const reset = () => {
        window.history.pushState(`/blog`, "", `/blog`);
        setCurrentQuery({ page: 1, category: null, tag: null });

    }

    const handleQueryChange = (key, value) => {
        window.history.pushState(`/blog?${key}=${value}`, "", `/blog?${key}=${value}`);

        if (!value) {
            reset();
        } else {
            if (key === "category") {
                setCurrentQuery({ page: 1, category: value, tag: null });

            }

            if (key === "tag") {
                setCurrentQuery({ page: 1, category: null, tag: value });

            }
        }

    }



    return (
        <React.Fragment>
            <Helmet>
                <title>{_w.meta_title}</title>
            </Helmet>
            <div className="blog-list-page">
                <SNSPanel />
                <div className="project-topic">
                    <div className="container">
                        <div className="topic inner-container">
                            <div className="title">
                                <h1 className="type-heading -en -h2">
                                    <span>{_w.heading}&nbsp;</span>
                                    <span className="highlight">{_w.heading_color}</span>
                                </h1>
                            </div>
                            <div className="illustration">
                                <img src="/images/blog-heading.svg" alt="" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="project-list">
                    <div className="container">
                        <div className="filter inner-container">
                            {currentTagLabel ? <div className="tag"><span>相關字詞：</span><b>{currentTagLabel}</b> <button onClick={reset}><Reset />重設</button></div> : (
                                <>
                                    <button className={`text-button ${currentQuery?.category === null ? '-active' : ''}`}
                                        onClick={() => handleQueryChange("category", null)}><span className="type-text -en -lg">All</span></button>
                                    {
                                        categories && categories.map((item, i) =>
                                            <button key={i}
                                                className={`text-button ${currentQuery?.category == item.id ? '-active' : ''}`}
                                                onClick={() => handleQueryChange("category", item.id)}
                                            ><span className="type-text -en -lg">{item.name}</span></button>
                                        )
                                    }
                                </>
                            )}
                        </div>
                        <div className="project-grid row">
                            {
                                blogs && blogs.map((item, i) =>
                                    <div className="item col-xl-4 col-lg-6 col-md-6" key={i}>
                                        <BlogCard blog={item} />
                                    </div>
                                )
                            }
                            {
                                blogs && blogs.length === 0 && loadingAnimation &&
                                <div className="project-empty type-heading -ch -h1">{_w.loading}</div>
                            }
                            {
                                blogs && blogs.length === 0 && !loadingAnimation &&
                                <div className="project-empty type-heading -ch -h1">{_w.empty}</div>
                            }
                        </div>
                    </div>
                </div>
                <div className="auto-load" ref={autoload} data-index="1">
                    {
                        loadingAnimation &&
                        <div className="loading-dot">
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                        </div>
                    }
                </div>
            </div>
        </React.Fragment>
    )
}

export default BlogList;